/** Combine conditional class names. Filters falsy values; no de-dup needed here. */
export function cn(...classes: (string | undefined | null | false | 0)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Formats a snake_case or underscore-separated label into a human-readable string.
 * Handles title-casing and special-case abbreviations.
 *
 * Special cases:
 *   github      → GitHub
 *   llm         → LLM
 *   prd         → PRD
 *   rd          → R&D
 *   ato         → ATO
 *   cc          → CC
 */
const SPECIAL_CASES: Record<string, string> = {
  github: 'GitHub',
  llm: 'LLM',
  prd: 'PRD',
  rd: 'R&D',
  ato: 'ATO',
  cc: 'CC',
};

export function formatLabel(value: string): string {
  return value
    .split('_')
    .map((word) => {
      const lower = word.toLowerCase();
      if (SPECIAL_CASES[lower]) return SPECIAL_CASES[lower];
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * Formats a stored income year (e.g. "FY2026") into the Australian cross-year
 * display format (e.g. "FY2025-26"). The ATO R&DTI documentation uses the
 * ending year in storage; the cross-year format makes the period clearer.
 */
export function formatFy(fy: string): string {
  const match = /^FY(\d{4})$/.exec(fy);
  if (!match) return fy;
  const endYear = parseInt(match[1]!, 10);
  return `FY${endYear - 1}-${String(endYear).slice(2)}`;
}

/**
 * Formats a developer name/email for display in the timewriting UI.
 * - GitHub bot accounts (e.g. dependabot[bot]) are shown as "App"
 * - Real names take priority over email addresses
 * - GitHub privacy emails (12345678+username@users.noreply.github.com) extract the username
 * - Falls back to the local part of an email address
 * - Returns "—" when no information is available
 */
export function formatDeveloper(
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  if (email?.includes('[bot]')) return 'App';
  if (name && name.trim().length > 0) return name.trim();
  if (email) {
    // GitHub privacy email: <id>+<username>@users.noreply.github.com → extract username
    const privacyMatch = /^\d+\+([^@]+)@users\.noreply\.github\.com$/.exec(email);
    if (privacyMatch?.[1]) return privacyMatch[1];
    return email.split('@')[0] ?? email;
  }
  return '—';
}
